import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChangePasswordModal from '../../modals/changePasswordModal/changePasswordModal';
import SignoutModal from '../../modals/signoutModal/signoutModal';
import { useDispatch, useSelector } from 'react-redux';
import DeleteProfileModal from '../../modals/deleteProfileModal/deleteProfileModal';
import { useTranslation } from 'react-i18next';
import '../../i18';
import Select from 'react-select';
import { languageStyles } from '../../common/customCss';
import { language } from '../../slices/settingsSlice';
import { getLocalStorageItem, setLocalStorageItem } from '../../common/LocalStorageService';
import { setLangCode, setLanguage, updateProfile } from '../../slices/authSlice';

const Header = () => {
    const { t, i18n } = useTranslation();
    const isLoggedIn = useSelector((state) => state.auth.userToken);
    const userInfo = useSelector((state) => state.auth.userInfo);
    const cartData = useSelector((state) => state.cart.cartData);
    const languages = useSelector((state) => state.settings.languageList);
    const navigate = useNavigate();
    const location = useLocation();
    const closeCanvasButton = useRef();

    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [isSignoutModalOpen, setIsSignoutModalOpen] = useState(false);
    const [isDeleteProfileModalOpen, setIsDeleteProfileModalOpen] = useState(false);
    const hideLinks = ["/sign-in", "/sign-up"].includes(location.pathname);
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        const storedLanguage = getLocalStorageItem("language");
        return storedLanguage?.lang_id || 1;
    });
    const [selectedLangCode, setSelectedLangCode] = useState(() => {
        const storedLanguage = getLocalStorageItem("language");
        return storedLanguage?.lang_code || "en";
    });
    const [selectedLangName, setSelectedLangName] = useState(() => {
        const storedLanguage = getLocalStorageItem("language");
        return storedLanguage?.name || "english";
    })

    const [isSticky, setIsSticky] = useState(false);
    const dispatch = useDispatch();

    const handleScroll = () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        setIsSticky(scrollTop >= 445);
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        dispatch(language({ limit: 10, sort: "name" }));
    }, [dispatch]);

    const handleLanguageChange = useCallback((event) => {
        const value = event.value;
        const code = event.code;
        const name = event.name;
        if (value !== selectedLanguage) {
            setSelectedLanguage(value);
            setSelectedLangCode(code);
            setSelectedLangName(name);
            setLocalStorageItem("language", { lang_id: value, lang_code: code, name: name });
            if (isLoggedIn) {
                const myPromise = new Promise((resolve, reject) => {
                    dispatch(
                        updateProfile({
                            payload: {
                                full_name: userInfo?.full_name,
                                country_code: userInfo?.country_code ? userInfo?.country_code : '',
                                phone_number: userInfo?.phone_number ? userInfo?.phone_number : '',
                                email: userInfo?.email,
                                language_id: value
                            },
                        })
                    )
                        .then((response) => {
                            if (response?.payload?.code == 200) {
                                setTimeout(() => {
                                    i18n.changeLanguage(code);
                                    dispatch(setLanguage({ lang_id: value, lang_code: code, name: name }));
                                    resolve(response.payload);
                                }, 1000);
                            }
                            else {
                                reject(response.payload)
                            }
                        })
                });
            }
            else {
                i18n.changeLanguage(code);
                dispatch(setLanguage({ lang_id: value, lang_code: code, name: name }));
            }
        }
    }, [selectedLangCode, i18n, selectedLanguage, selectedLangName, userInfo]);

    if (isLoggedIn) dispatch(setLanguage({ lang_code: userInfo?.user_language?.code, lang_id: userInfo?.user_language?.id, name: userInfo?.user_language?.name }));

    useEffect(() => {
        // Ensure the correct language is applied on mount
        const storedLanguage = getLocalStorageItem("language");
        if (storedLanguage) {
            setSelectedLanguage(storedLanguage.lang_id);
            setSelectedLangCode(storedLanguage.lang_code);
            setSelectedLangName(storedLanguage.name);
            i18n.changeLanguage(storedLanguage.lang_code);
        } else {
            i18n.changeLanguage("en");
        }
    }, [i18n]);

    useEffect(() => {
        if (isLoggedIn) {
            setSelectedLangName(userInfo?.user_language?.name);
            setSelectedLanguage(userInfo?.user_language?.id);
            setSelectedLangCode(userInfo?.user_language?.code);
        }
    }, [userInfo])

    return (
        <>
            {isLoggedIn ? (
                <>
                    <header>
                        <nav className={`navbar navbar-area-1 navbar-area navbar-expand-lg ${isSticky ? "sticky-active" : ""
                            }`}>
                            <div className="container nav-container">
                                <div className="logo">
                                    <Link to="/home" className='HeaderLogoLink'>
                                        <img src={process.env.PUBLIC_URL + "/assets/img/logo-new.svg"} alt="img" />
                                    </Link>
                                </div>
                                <div className="ResponsiveLoginandToggleMain">
                                    <div className="DNoneBoxOnResponsive">
                                        <div className="collapse navbar-collapse" id="edumint_main_menu">
                                            <ul className="navbar-nav menu-open">
                                                <li className="current-menu-item">
                                                    <Link to="/home">
                                                        {t('Home')}
                                                    </Link>
                                                </li>
                                                <li className="menu-item-has-item">
                                                    <Link to="/course">
                                                        {t('courseHeading')}
                                                    </Link>
                                                </li>
                                                <li className="current-menu-item">
                                                    <Link to="/community-forum">
                                                        {t('communityForum')}
                                                    </Link>
                                                </li>
                                                <li className="menu-item-has-item">
                                                    <Link to="/blog">
                                                        {t('blogsHeading')}
                                                    </Link>
                                                </li>
                                                <li className="menu-item-has-item">
                                                    <Link to="/chat">
                                                        {t('chat')}
                                                    </Link>
                                                </li>
                                                {/* <li className="menu-item-has-children">
                                                    <a href="javascript:void(0)">{t('Pages')}</a>
                                                    <ul className="sub-menu">
                                                        <li>
                                                            <Link to="/about-us">{t('aboutUs')}</Link>
                                                        </li>
                                                        <li>
                                                            <Link to="/faq">{t('faq')}</Link>
                                                        </li>
                                                        <li>
                                                            <Link to="/tandc">{t('TnC')}</Link>
                                                        </li>
                                                        <li>
                                                            <Link to="/privacy-policy">{t('privacyPolicy')}</Link>
                                                        </li>
                                                    </ul>
                                                </li> */}
                                                <li>
                                                    <Link to="/contact-us">
                                                        {t('contactUsHeading')}
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Select name='language' id='language'
                                                        placeholder={t('selectFieldName', { field: t("Language") })}
                                                        value={languages?.data?.find(language => language.id === selectedLanguage) ? { value: selectedLanguage, label: languages.data.find(language => language.id === selectedLanguage).native_name } : null}
                                                        onChange={handleLanguageChange}
                                                        options={
                                                            languages?.data?.map((val) => (
                                                                { value: val.id, label: val.native_name, code: val.code, name: val.name }
                                                            ))
                                                        }
                                                        styles={languageStyles}
                                                    />
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className='ResponsiveLoginandToggleMain'>
                                        <div className="ProfileMainBox header-cart-css flex-shrink-0">
                                            <Link to={"/cart"} className="cart-link flex-shrink-0">
                                                <img
                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/cart.svg"}
                                                    alt="profileuserimg"
                                                    className="profileuserimg borderFiveZero imgCover"
                                                    height={32}
                                                    width={32}
                                                />
                                                {cartData?.quantity > 0 &&
                                                    <span>{cartData?.quantity}</span>
                                                }
                                            </Link>
                                            <div className="dropdown">
                                                <a href="" className="ProfileLink" data-bs-toggle="dropdown" aria-expanded="false">
                                                    <img src={userInfo.image || process.env.PUBLIC_URL + "/assets/img/icon/default.jpg"} alt="profileuserimg" className="profileuserimg borderFiveZero imgCover" />
                                                </a>
                                                <ul className="dropdown-menu">
                                                    <li>
                                                        <Link to="/profile" className="dropdown-item">
                                                            <img
                                                                src={process.env.PUBLIC_URL + "/assets/img/icon/user-circle.svg"}
                                                                alt="accountIcon"
                                                                className="IconTwoZero"
                                                            />
                                                            {t('accSetting')}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link to="/test" className="dropdown-item">
                                                            <img
                                                                src={process.env.PUBLIC_URL + "/assets/img/icon/test.svg"}
                                                                alt="Cpicon"
                                                                className="IconTwoZero"
                                                            />
                                                            {t('myTest')}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link to="/favourite-course" className="dropdown-item">
                                                            <i className="fa-regular fa-bookmark IconTwoZero" />
                                                            {t('favCoursesHeading')}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <button className="dropdown-item" onClick={() => setIsChangePasswordModalOpen(true)} >
                                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/Cpicon.svg"} alt="Cpicon" className="IconTwoZero" />
                                                            {t('changePasswordModalHeading')}
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <Link to="/notification" className="dropdown-item">
                                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/notificationbell.svg"} alt="notificationbell" className="IconTwoZero" />
                                                            {t('Notifications')}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <button className="dropdown-item" onClick={() => setIsDeleteProfileModalOpen(true)}>
                                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/deleteIcon.svg"} alt="deleteIcon" className="IconTwoZero" />
                                                            {t('deleteProfileModalHeading')}
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button className="dropdown-item" onClick={() => setIsSignoutModalOpen(true)}>
                                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/SignOutIcon.svg"} alt="SignOut" className="IconTwoZero" />
                                                            {t('signOut')}
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="responsive-mobile-menu flex-shrink-0">
                                            <button className="menu toggle-btn d-block d-xl-none" data-bs-toggle="offcanvas" href="#HeaderOffcanvas">
                                                <img src={process.env.PUBLIC_URL + "/assets/img/icon/toggleimg.svg"} alt="toggleimg" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </nav>
                        <ChangePasswordModal
                            isOpen={isChangePasswordModalOpen}
                            onClose={() => setIsChangePasswordModalOpen(false)}
                        />
                        <SignoutModal
                            isOpen={isSignoutModalOpen}
                            onClose={() => setIsSignoutModalOpen(false)}
                        />
                        <DeleteProfileModal
                            isOpen={isDeleteProfileModalOpen}
                            onClose={() => setIsDeleteProfileModalOpen(false)}
                        />
                    </header>
                    <div className="HeaderOffcanvas">
                        <div
                            className="offcanvas offcanvas-start"
                            tabIndex={-1}
                            id="HeaderOffcanvas"
                            aria-labelledby="HeaderOffcanvas"
                        >
                            <div className="offcanvas-header">
                                <Link to="/home" className="OffcanvvasLogo HeaderLogoLink">
                                    <img src={process.env.PUBLIC_URL + "/assets/img/logo-new.svg"} alt="img" />
                                </Link>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="offcanvas"
                                    aria-label="Close"
                                    ref={closeCanvasButton}
                                />
                            </div>
                            <div className="offcanvas-body">
                                <ul className="OffcanvasUl">
                                    <li className="current-menu-item">
                                        <Link to="/home"
                                            onClick={() => closeCanvasButton.current.click()}
                                        >{t('Home')}</Link>
                                    </li>
                                    <li className="current-menu-item">
                                        <Link to="/community-forum"
                                            onClick={() => closeCanvasButton.current.click()}>
                                            {t('communityForum')}
                                        </Link>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <Link to="/course"
                                            onClick={() => closeCanvasButton.current.click()}>
                                            {t('courseHeading')}
                                        </Link>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <Link to="/blog"
                                            onClick={() => closeCanvasButton.current.click()}>
                                            {t('blogsHeading')}
                                        </Link>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <Link to="/chat"
                                            onClick={() => closeCanvasButton.current.click()}>
                                            {t('chat')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/about-us"
                                            onClick={() => closeCanvasButton.current.click()}>{t('aboutUs')}</Link>
                                    </li>
                                    <li>
                                        <Link to="/faq"
                                            onClick={() => closeCanvasButton.current.click()}>{t('faq')}</Link>
                                    </li>
                                    <li>
                                        <Link to="/tandc"
                                            onClick={() => closeCanvasButton.current.click()}>{t('TnC')}</Link>
                                    </li>
                                    <li>
                                        <Link to="/privacy-policy"
                                            onClick={() => closeCanvasButton.current.click()}>{t('privacyPolicy')}</Link>
                                    </li>
                                    <li>
                                        <Link to="/contact-us"
                                            onClick={() => closeCanvasButton.current.click()}>
                                            {t('contactUsHeading')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Select name='language' id='language'
                                            placeholder={t('selectFieldName', { field: t("Language") })}
                                            value={languages?.data?.find(language => language.id === selectedLanguage) ? { value: selectedLanguage, label: languages.data.find(language => language.id === selectedLanguage).native_name } : null}
                                            onChange={handleLanguageChange}
                                            options={
                                                languages?.data?.map((val) => (
                                                    { value: val.id, label: val.native_name, code: val.code, name: val.name }
                                                ))
                                            }
                                            styles={languageStyles}
                                        />
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <header className="header">
                        <nav className={`navbar-expand-lg`}>
                            <div className="container">
                                {/* <div className="row"> */}
                                {/* <div className="col-12"> */}
                                <div className="Navbar">
                                    <div className="logo">
                                        <Link to="/" className="HeaderLogoLink">
                                            <img src={process.env.PUBLIC_URL + "/assets/img/logo-new.svg"} alt="img" />
                                        </Link>
                                    </div>
                                    <div className="responsive-mobile-menu">
                                        <button className="menu toggle-btn d-block d-xl-none" data-bs-toggle="offcanvas" href="#HeaderOffcanvas1">
                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/toggleimg.svg"} alt="toggleimg" />
                                        </button>
                                    </div>
                                    <div className="nav-right-part nav-right-part-desktop">
                                        {!hideLinks && (
                                            <>
                                                <a href="#about-us"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("about-us").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}>
                                                    <button className="signin-btn btn">{t('aboutUs')}</button>
                                                </a>
                                                <a href="#top-featured-courses"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("top-featured-courses").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}
                                                >
                                                    <button className="signin-btn btn">{t('courseHeading')}</button>
                                                </a>
                                                <a href="#how-it-works"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("how-it-works").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}>
                                                    <button className="signin-btn btn">{t('howItWorks')}</button>
                                                </a>
                                                <a href="#our-creative-team"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("our-creative-team").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}>
                                                    <button className="signin-btn btn">{t('team')}</button>
                                                </a>
                                                <a href="#blogs"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("blogs").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}>
                                                    <button className="signin-btn btn">{t('blogsHeading')}</button>
                                                </a>
                                                <a href="#contact-us"
                                                    onClick={() => {
                                                        navigate('/')
                                                        setTimeout(() => {
                                                            document.getElementById("contact-us").scrollIntoView({ behavior: "smooth" });
                                                        }, 100);
                                                    }}>
                                                    <button className="signin-btn btn">{t('contactUsHeading')}</button>
                                                </a>
                                                <hr />
                                            </>
                                        )}
                                        <Link to="/sign-in">
                                            <button className="signin-btn btn">{t('signIn')}</button>
                                        </Link>
                                        <Link to="/sign-up">
                                            <button className="btn btn-base">{t('signUp')}</button>
                                        </Link>
                                        <Select name='language' id='language'
                                            placeholder={t('selectFieldName', { field: t("Language") })}
                                            value={languages?.data?.find(language => language.id === selectedLanguage) ? { value: selectedLanguage, label: languages.data.find(language => language.id === selectedLanguage).native_name } : null}
                                            onChange={handleLanguageChange}
                                            options={
                                                languages?.data?.map((val) => (
                                                    { value: val.id, label: val.native_name, code: val.code, name: val.name }
                                                ))
                                            }
                                            styles={languageStyles}
                                        />
                                    </div>
                                </div>
                                {/* </div> */}
                                {/* </div> */}
                            </div>
                        </nav>
                    </header>
                    <div className="HeaderOffcanvas1">
                        <div
                            className="offcanvas offcanvas-start"
                            tabIndex={-1}
                            id="HeaderOffcanvas1"
                            aria-labelledby="HeaderOffcanvas1"
                        >
                            <div className="offcanvas-header">
                                <Link to="/home" className="OffcanvvasLogo HeaderLogoLink">
                                    <img src={process.env.PUBLIC_URL + "/assets/img/logo-new.svg"} alt="img" />
                                </Link>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="offcanvas"
                                    aria-label="Close"
                                />
                            </div>
                            <div className="offcanvas-body">
                                <ul className="OffcanvasUl">
                                    <li className="current-menu-item">
                                        <a href="#about-us"

                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("about-us").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}>
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('aboutUs')}</button>
                                        </a>
                                    </li>
                                    <li className="current-menu-item">
                                        <a href="#top-featured-courses"
                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("top-featured-courses").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}
                                        >
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('featuredCoursesHeading')}</button>
                                        </a>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <a href="#how-it-works"
                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("how-it-works").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}>
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('howItWorks')}</button>
                                        </a>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <a href="#our-creative-team"
                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("our-creative-team").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}>
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('team')}</button>
                                        </a>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <a href="#blogs"
                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("blogs").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}>
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('blogsHeading')}</button>
                                        </a>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <a href="#contact-us"
                                            onClick={() => {
                                                navigate('/')
                                                setTimeout(() => {
                                                    document.getElementById("contact-us").scrollIntoView({ behavior: "smooth" });
                                                }, 100);
                                            }}>
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('contactUsHeading')}</button>
                                        </a>
                                    </li>
                                    <hr />
                                    <li className="menu-item-has-item">
                                        <Link to="/sign-in">
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('signIn')}</button>
                                        </Link>
                                    </li>
                                    <li className="menu-item-has-item">
                                        <Link to="/sign-up">
                                            <button className="signin-btn btn" data-bs-dismiss="offcanvas">{t('signUp')}</button>
                                        </Link>
                                    </li>
                                    <li>
                                        <Select name='language' id='language'
                                            placeholder={t('selectFieldName', { field: t("Language") })}
                                            value={languages?.data?.find(language => language.id === selectedLanguage) ? { value: selectedLanguage, label: languages.data.find(language => language.id === selectedLanguage).native_name } : null}
                                            onChange={handleLanguageChange}
                                            options={
                                                languages?.data?.map((val) => (
                                                    { value: val.id, label: val.native_name, code: val.code, name: val.name }
                                                ))
                                            }
                                            styles={languageStyles}
                                        />
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default Header;